import type { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import type Item from '../@types/item';

import type { ElasticSearchResponse } from '../@types/ElasticSearchResponse';
import type { BaseAction } from '../@types/actions';
import * as http from '../Http';
import { buildGetRequest } from '../Http/requestTemplates';
import type QueryFilter from '../helpers/query-filter';
import requestCache from '../utils/request-cache';

export const REQ_ITEM = 'REQ/ITEM';
export const RCV_ITEM = 'RCV/ITEM';
type REQ_ITEM_TYPE = BaseAction<typeof REQ_ITEM>;
type RCV_ITEM_TYPE = BaseAction<typeof RCV_ITEM> & {
  response: ElasticSearchResponse<Item>;
};

export const REQ_ITEM_FACETS = 'REQ/ITEM_FACETS';
export const RCV_ITEM_FACETS = 'RCV/ITEM_FACETS';
type REQ_ITEM_FACETS_TYPE = BaseAction<typeof REQ_ITEM_FACETS>;
type RCV_ITEM_FACETS_TYPE = BaseAction<typeof RCV_ITEM_FACETS> & {
  response: ElasticSearchResponse<Item>;
};

export const REQ_RELATED_ITEMS = 'REQ/ITEM_RELATED';
export const RCV_RELATED_ITEMS = 'RCV/ITEM_RELATED';
type REQ_RELATED_ITEMS_TYPE = BaseAction<typeof REQ_RELATED_ITEMS>;
type RCV_RELATED_ITEMS_TYPE = BaseAction<typeof RCV_RELATED_ITEMS> & {
  response: ElasticSearchResponse<Item>;
};

export const REQ_CLEAR = 'REQ/ITEM_CLEAR';
type REQ_CLEAR_TYPE = BaseAction<typeof REQ_CLEAR>;

export const REQUEST_FAILED = 'ITEM/REQUEST_FAILED';
type REQUEST_FAILED_TYPE = BaseAction<typeof REQUEST_FAILED> & {
  action: typeof REQ_ITEM | typeof REQ_ITEM_FACETS | typeof REQ_RELATED_ITEMS;
};

export type ITEM_ACTION =
  | REQ_ITEM_TYPE
  | RCV_ITEM_TYPE
  | REQ_ITEM_FACETS_TYPE
  | RCV_ITEM_FACETS_TYPE
  | REQ_RELATED_ITEMS_TYPE
  | RCV_RELATED_ITEMS_TYPE
  | REQ_CLEAR_TYPE
  | REQUEST_FAILED_TYPE;
type Dispatch = ReduxDispatch<ITEM_ACTION>;

export const find = (id: string) => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_ITEM)) {
    return;
  }
  requestCache.isPending(REQ_ITEM, true);

  try {
    dispatch({ type: REQ_ITEM });

    const response = await http.json<ElasticSearchResponse<Item>>(
      `/api/search/${id}`,
      buildGetRequest({ scrapeProbabilityScore: true }),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_ITEM,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_ITEM });
  }
  requestCache.isPending(REQ_ITEM, false);
};

export const callbackFind = async (id: string, callback: (data: Item) => void) => {
  try {
    const response = await http.json<ElasticSearchResponse<Item>>(
      `/api/search/${id}`,
      buildGetRequest({ scrapeProbabilityScore: true }),
    );
    callback(response.response.documentList.documents[0]);
  } catch (_e) {
    // Ignore
  }
};

const getFilterQueryString = (queryFilter: QueryFilter) => {
  if (queryFilter.isEmpty()) return '';

  return `?filter=${queryFilter.toQueryStringFilter()}`;
};

export const facets = (queryFilter: QueryFilter) => async (dispatch: Dispatch) => {
  if (requestCache.isPending(REQ_ITEM_FACETS)) {
    return;
  }
  requestCache.isPending(REQ_ITEM_FACETS, true);

  try {
    dispatch({ type: REQ_ITEM_FACETS });

    const response = await http.json<ElasticSearchResponse<Item>>(
      `/api/facets${getFilterQueryString(queryFilter)}`,
      buildGetRequest({ scrapeProbabilityScore: true }),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_ITEM_FACETS,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_ITEM_FACETS });
  }
  requestCache.isPending(REQ_ITEM_FACETS, false);
};

export const related = (item: Item) => async (dispatch: Dispatch) => {
  if (!item || !item.branches || item.branches.length === 0) return;

  if (requestCache.isPending(REQ_RELATED_ITEMS)) {
    return;
  }
  requestCache.isPending(REQ_RELATED_ITEMS, true);

  try {
    dispatch({ type: REQ_RELATED_ITEMS });

    const response = await http.json<ElasticSearchResponse<Item>>(
      `/api/related/${item.id}?branchId=${item.branches[0].id}`,
      buildGetRequest({ scrapeProbabilityScore: true }),
    );
    http.validateResponse(response);
    dispatch({
      type: RCV_RELATED_ITEMS,
      response: response.response,
    });
  } catch (_e) {
    dispatch({ type: REQUEST_FAILED, action: REQ_RELATED_ITEMS });
  }
  requestCache.isPending(REQ_RELATED_ITEMS, false);
};

export const clear = () => (dispatch: Dispatch) => dispatch({ type: REQ_CLEAR });
